/**
 * Composes the full FullCalendar events array for manual scheduling mode.
 * Merges: booked slot + external busy times + past background.
 *
 * Ref: KInterviewScheduler/.../useCalendarEvents.tsx
 */
import { useMemo } from 'react';
import moment from 'moment-timezone';
import type { EventInput } from '@fullcalendar/core';
import {
  EVENT_STYLES,
  EVENT_PRIORITY,
  BG_EVENT_CLASSES,
  resolveExternalEventStyle,
} from '../events/eventStyles';
import type { AllDayChipEvent } from '../../../components/AllDayEventChip';
import { buildAllDayBlockedOverlayEvents } from '../../../components/allDayBlockedOverlay';
import { splitEventAcrossDays } from '../events/eventHelpers';
import { partitionAllDayEvents } from '@/api/calendarEvents';
import type { ScheduleSlot } from '../../state/useScheduleSharedState';

/** Busy blocks passed from `CalendarPanel` into FullCalendar via `useManualCalendarEvents`. */
export interface ManualCalendarExternalEvent {
  id?: string;
  start: string;
  end: string;
  title?: string;
  backgroundColor?: string;
  borderColor?: string;
  textColor?: string;
  /** Override the default `im-interviewer-busy` class — meeting rooms
   *  pass `im-meeting-room-busy` so CSS can render the muted palette. */
  classNames?: string;
  /** Richer title shown only on hover. Meeting-room events keep the
   *  friendly room name in `title` and stash the real meeting subject
   *  here so users can still see what blocked the room. */
  tooltipTitle?: string;
  /** Original status from the BE (`'busy' | 'oof' | 'unknown' | 'free'`).
   *  `'free'` flips the render to a dotted-border ghost block (BE sends
   *  user-marked-free events; legacy hid them entirely — we show them
   *  for visibility while keeping them out of slot math). Algorithms
   *  drop `'free'` upstream in `useBusyCalendarEvents.interviewerBusyForAlgorithm`. */
  status?: string;
}

interface UseManualCalendarEventsOptions {
  bookedSlot: ScheduleSlot | null;
  externalEvents?: ManualCalendarExternalEvent[];
  timezone: string;
  interviewName?: string;
  /** `YYYY-MM-DD` → the gap-finder still offers a slot that day. Keeps the
   *  all-day wash off days the blue "Available" bars are painted on. */
  isDayBookable?: (_dateKey: string) => boolean;
}

export function useManualCalendarEvents({
  bookedSlot,
  externalEvents = [],
  timezone,
  interviewName,
  isDayBookable,
}: UseManualCalendarEventsOptions) {
  // Split once, then fork: timed blocks go to the grid, all-day events to
  // the day-header chips. See the note in the events memo below.
  const { allDayExternalEvents, timedExternalEvents } = useMemo(() => {
    const split = externalEvents.flatMap((evt) =>
      splitEventAcrossDays(evt, timezone)
    );
    const { allDay, timed } = partitionAllDayEvents(split, timezone);
    return { allDayExternalEvents: allDay, timedExternalEvents: timed };
  }, [externalEvents, timezone]);

  /**
   * All-day chips keyed by `YYYY-MM-DD` in the calendar timezone, already
   * carrying the same resolved colours/class as their grid block would.
   */
  const allDayEventsByDateKey = useMemo(() => {
    const byKey: Record<string, AllDayChipEvent[]> = {};
    for (let i = 0; i < allDayExternalEvents.length; i += 1) {
      const evt = allDayExternalEvents[i];
      const key = moment.tz(evt.start, timezone).format('YYYY-MM-DD');
      (byKey[key] ??= []).push({
        id: evt.id ? `${evt.id}-${i}` : `all-day-${evt.start}-${i}`,
        title: evt.title ?? 'Busy',
        tooltipTitle: evt.tooltipTitle,
        isFree: evt.status === 'free',
        ...resolveExternalEventStyle(evt),
      });
    }
    return byKey;
  }, [allDayExternalEvents, timezone]);

  const events = useMemo((): EventInput[] => {
    const evts: EventInput[] = [];

    // 1. External events (interviewer busy times / meeting-room blocks).
    // Foreground events so FullCalendar's column-split algorithm keeps
    // overlapping blocks side-by-side. Clicks are handled by the inline
    // onClick on BusyEventContent (mirrors legacy BusyEventContent.tsx).
    //
    // Multi-day events are split at midnight so each per-day segment
    // carries its own start/end time — without this, a 24h OOO from
    // Mon 9:30 PM → Tue 9:30 PM renders as "9:30 PM – 9:30 PM" on both
    // segments (FC's default per-segment label uses the global event
    // bounds). Matches the legacy preprocessing in
    // `schProcessCalendarEvents.ts` (now under
    // `components/shared/interview-scheduler/`).
    //
    // All-day events are pulled out AFTER the split (so multi-day spans are
    // already per-day) and rendered as day-header chips instead — a
    // full-height block otherwise swallows the whole column. Render-only:
    // they still subtract from availability via
    // `useBusyCalendarEvents.interviewerBusyForAlgorithm`.
    for (let i = 0; i < timedExternalEvents.length; i += 1) {
      const evt = timedExternalEvents[i];
      const isFree = evt.status === 'free';
      // Shared with the all-day header chips — see `resolveExternalEventStyle`.
      const style = resolveExternalEventStyle(evt);
      evts.push({
        id: evt.id ? `${evt.id}-${i}` : `ext-${evt.start}-${evt.end}`,
        start: evt.start,
        end: evt.end,
        title: evt.title ?? 'Busy',
        // Free events render as background — they tint the cell behind
        // busy blocks without claiming a foreground column. Dashed
        // border + opacity still come from `.im-interviewer-free` CSS.
        display: isFree ? 'background' : undefined,
        backgroundColor: style.backgroundColor,
        borderColor: style.borderColor,
        textColor: style.textColor,
        classNames: style.className,
        editable: false,
        extendedProps: {
          category: 'busy',
          priority: EVENT_PRIORITY.busy,
          tooltipTitle: evt.tooltipTitle,
          status: evt.status,
        },
      });
    }

    // 2. Booked slot — editable, draggable, resizable
    if (bookedSlot?.start && bookedSlot?.end) {
      evts.push({
        id: 'booked-slot',
        start: bookedSlot.start,
        end: bookedSlot.end,
        title: interviewName || 'Interview',
        backgroundColor: EVENT_STYLES.booked.backgroundColor,
        borderColor: EVENT_STYLES.booked.borderColor,
        textColor: EVENT_STYLES.booked.textColor,
        editable: true,
        durationEditable: true,
        startEditable: true,
        classNames: 'booked-event',
        extendedProps: {
          category: 'booked',
          priority: EVENT_PRIORITY.booked,
        },
      });
    }

    // 3. Past time background — grey overlay for all past times
    evts.push({
      start: moment()
        .tz(timezone)
        .subtract(10, 'year')
        .format('YYYY-MM-DDTHH:mm:ssZ'),
      end: moment().tz(timezone).format('YYYY-MM-DDTHH:mm:ssZ'),
      display: 'background',
      classNames: BG_EVENT_CLASSES.disabledSlots,
      color: 'transparent',
    });

    // 4. Grey wash over each chipped day the gap-finder has nothing left on —
    // the chip alone is easy to miss now that the full-height block is gone.
    // A day still offering slots stays clear, so the wash can never contradict
    // the blue "Available" bars painted over it. Visual only; picking stays
    // open (free all-day events must not block).
    evts.push(
      ...buildAllDayBlockedOverlayEvents({
        chipsByDateKey: allDayEventsByDateKey,
        timezone,
        isDayBookable,
      })
    );

    return evts;
  }, [
    bookedSlot,
    timedExternalEvents,
    allDayEventsByDateKey,
    timezone,
    interviewName,
    isDayBookable,
  ]);

  return { events, allDayEventsByDateKey };
}
